import React, { useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import ChevronRight from '../../icons/ChevronRight';
import './Blog.css';
import { useTranslation } from 'react-i18next';
import useLanguagePrefix from '../../LanguagePrefix';
import { useNavigate } from 'react-router-dom';
import Pagination from '@mui/material/Pagination';

function Blog() {
    const { t } = useTranslation();
    const { pageNum } = useParams();
    const langPrefix = useLanguagePrefix();
    const navigate = useNavigate();
    const [articles, setArticles] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [trendingArticles, setTrendingArticles] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [currentPage, setCurrentPage] = useState(pageNum ? parseInt(pageNum, 10) : 1);
    const [totalPages, setTotalPages] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const apiUrl = "https://api.prostay.com";
    // const apiUrl = "https://localhost:7167";

    const [initialLoading, setInitialLoading] = useState(false);
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setInitialLoading(true);
            try {
                const [articlesResult, categoriesResult, trendingResult] = await Promise.all([
                    //   fetchPaginatedArticles("", 10, 1, null),
                    fetchCategories(),
                    fetchTrendingArticles()
                ]);
            } catch (err) {
                console.error('Error fetching blog data:', err);
            } finally {
                setLoading(false);
                setInitialLoading(false);
            }
        };

        fetchData();
    }, []);


    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            const saved = localStorage.getItem('blogSearchTerm') ?? '';
            if (saved) {
                setSearchInput(saved);
                setSearchTerm(saved);
            }
            localStorage.removeItem('blogSearchTerm');
            setCurrentPage(pageNum ? parseInt(pageNum, 10) : 1);
            try {
                await fetchPaginatedArticles(saved || '', pageSize, pageNum || 1, null)
            } catch (err) {
                console.error('Error fetching blog data:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [pageNum]);

    const fetchPaginatedArticles = async (_searchTerm, _pageSize, _currentPage, _categoryId) => {
        setSearchInput(_searchTerm);
        try {
            const requestPayload = {
                searchTerm: _searchTerm,
                pageSize: _pageSize,
                currentPage: _currentPage,
                categoryId: _categoryId
            };

            const res = await fetch(apiUrl + '/WebsiteKnowledgebase/get-blog-articles-paginated', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(requestPayload)
            });

            if (!res.ok) throw new Error('Failed to fetch paginated articles');

            const responseData = await res.json();

            setArticles(responseData.data);
            setTotalPages(responseData.totalPages);
        } catch (err) {
            console.error('Error loading blog articles:', err);
        }
    };

    const fetchTrendingArticles = async () => {
        try {

            const resTrending = await fetch(apiUrl + '/WebsiteKnowledgebase/get-trending-blog-articles');
            if (!resTrending.ok) throw new Error('Failed to fetch trending articles');
            const dataTrending = await resTrending.json();
            setTrendingArticles(dataTrending);
        } catch (err) {
            console.error('Error loading trending articles:', err);
        }
    };


    const fetchCategories = async () => {
        try {

            const resTrending = await fetch(apiUrl + '/WebsiteKnowledgebase/get-blog-articles-categories');
            if (!resTrending.ok) throw new Error('Failed to fetch trending articles');
            const categories = await resTrending.json();
            setCategories(categories);
        } catch (err) {
            console.error('Error loading trending articles:', err);
        }
    };


    const handlePageChange = async (event, newPage) => {
        navigate(`${langPrefix}/blog/page/${newPage}/`);
    };

    const getPreview = (htmlContent) => {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = htmlContent;
        let plainText = tempDiv.textContent || tempDiv.innerText || '';
        plainText = plainText.replace(/\[Fig_\d+\]/g, '');
        return plainText.length > 130 ? plainText.substring(0, 130).trim() + '...' : plainText.trim();
    };

    const canonicalUrl = `https://www.prostay.com${langPrefix}/blog/`;

    const pageTitle = pageNum ? `Hotel PMS Insights Blog, Trends & Expert Tips - Page ${pageNum || '1'} - Prostay` : "Hotel PMS Insights Blog, Trends & Expert Tips - Prostay";
    const pageDescription = 'Read hotel tech trends, expert tips, and news from the Prostay team. Learn how to optimize your operations and guest experience.';

    function formatDate(date) {
        if (!date) return '';
        const d = new Date(date);
        return d.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
        });
    }

    const handleCategoryClick = (catName) => {
        const slugified = catName.toLowerCase().replace(/\s+/g, '-');
        navigate(`${langPrefix}/blog/${catName.toLowerCase().replace(/\s+/g, '-')}`);
    };

    const handlePerformSearch = async (term) => {
        setSearchTerm(term.trim());

        if (pageNum !== '1') {
            if (term.trim()) {
                localStorage.setItem('blogSearchTerm', term.trim());
            } else {
                localStorage.removeItem('blogSearchTerm');
            }
            navigate(`${langPrefix}/blog/page/1/`);
        } else {
            setLoading(true);
            await fetchPaginatedArticles(term.trim(), pageSize, 1, null);
            setLoading(false);
        }
    };

    const getArticleTitle = (article) => {
        const cleanLangPrefix = (langPrefix || '').replace(/\//g, '');

        if (!cleanLangPrefix || cleanLangPrefix === 'en') {
            return article.title;
        }
        return article.titleTranslations?.[cleanLangPrefix] || article.title;
    }

    const getArticleContent = (article) => {
        const cleanLangPrefix = (langPrefix || '').replace(/\//g, '');

        if (!cleanLangPrefix || cleanLangPrefix === 'en') {
            return article.content;
        }

        return article.contentTranslations?.[cleanLangPrefix] || article.content;
    }

    const getArticleSlug = (article) => {
        const cleanLangPrefix = (langPrefix || '').replace(/\//g, '');

        if (!cleanLangPrefix || cleanLangPrefix === 'en') {
            return article.slug;
        }
        return article.slugTranslations?.[cleanLangPrefix] || article.slug;
    }

    return (
        <div className="blog">
            <Helmet>
                <title>{pageTitle}</title>
                <meta name="description" content={pageDescription} />
                <link rel="canonical" href={canonicalUrl} />
                <link rel="alternate" href={"https://www.prostay.com/en" + `/blog/`} />
                <link rel="alternate" href={"https://www.prostay.com/es" + `/blog/`} />


                <meta property="og:title" content={pageTitle} />
                <meta property="og:description" content={pageDescription} />
                <meta property="og:type" content="website" />
                <meta property="og:url" content={canonicalUrl} />
                <meta property="og:image" content='https://prostay.com/images/ogImage.png' />

                <meta property="og:site_name" content="Prostay" />

                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={pageTitle} />
                <meta name="twitter:description" content={pageDescription} />
                <meta name="twitter:image" content='https://prostay.com/images/ogImage.png' />
                <meta name="twitter:site" content="@prostaypms" />
                <meta name="twitter:creator" content="@prostaypms" />

            </Helmet>



            {(loading || initialLoading) ? (
                <div className="d-flex align-items-center justify-content-center mt-5" style={{ minHeight: 'calc(100vh - 70px)' }}>
                    <div className="spinner-container">
                        <div className="custom-spinner"></div>
                    </div>
                </div>
            ) : (
                <>
                    <div className="background-image">
                        <div className="background-image-start">
                            <div className="d-flex align-items-center gap-2 gap-md-3 breadcrumbs">
                                <a href={`${langPrefix}/`} style={{ textDecoration: 'none' }}><small>{t('Home')}</small></a>
                                <ChevronRight />
                                <a href={`${langPrefix}/blog/`} style={{ textDecoration: 'none' }}>
                                    <small className='text-primary'>{t('Blog')}</small>
                                </a>
                            </div>
                            <div className="mt-4">
                                <h1>{t('Prostay Blog')}</h1>
                            </div>
                        </div>
                    </div>
                    <div className="d-flex flex-column align-items-center justify-content-center">
                        <div className='d-flex mt-4 mb-1 justify-content-end page-content'>
                            <div className='search-container'>
                                <input
                                    value={searchInput}
                                    onChange={(e) => setSearchInput(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            handlePerformSearch(searchInput);
                                        }
                                    }}
                                    placeholder='Search'
                                />

                                <div onClick={() => {
                                    handlePerformSearch(searchInput);
                                }} style={{ cursor: 'pointer' }}>
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M21 21L16.7 16.7M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z" stroke="#7784A3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </div>
                            </div>
                        </div>
                        <div className="content-grid page-content mt-2 mt-md-3">
                            <div className='col'>
                                <div className="white-content-card" style={{ height: 'fit-content' }}>
                                    <div className="white-content-card p-0 d-none d-md-flex flex-column">
                                        <h5 className='m-0' style={{ fontFamily: 'Inter' }}>{t('Popular Categories')}</h5>
                                        <hr />
                                        {categories.map((cat) => {
                                            const slug = cat.name.toLowerCase().replace(/\s+/g, "-");

                                            return (
                                                <a
                                                    key={cat.name}
                                                    href={`${langPrefix}/blog/${slug}/`}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        navigate(`${langPrefix}/blog/${slug}/`);
                                                    }}
                                                    className={`subcategory-link w-100 `}
                                                    style={{ textDecoration: "none" }}
                                                >
                                                    {cat.name}
                                                </a>
                                            );
                                        })}
                                    </div>
                                    <div className='d-block d-md-none'>
                                        <h5 className='m-0' style={{ fontFamily: 'Inter' }}>{t('Popular Categories')}</h5>
                                        <hr />
                                        <div className='select-container w-100' style={{ position: 'relative' }}>
                                            <select
                                                className="form-select w-100"
                                                onChange={(e) => handleCategoryClick(e.target.value)}
                                            >
                                                <option value="">{t('Select a category')}</option>
                                                {categories.map((cat) => (
                                                    <option key={cat.name} value={cat.name}>
                                                        {cat.name}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                </div>

                                <div className="white-content-card mt-3" style={{ height: 'fit-content' }}>
                                    <h5 className='m-0' style={{ fontFamily: 'Inter' }}>{t('Trending Post')}</h5>
                                    <hr />
                                    {
                                        trendingArticles?.map((article, index) =>
                                            <a className='d-flex gap-2 trending-article-card' style={{ textDecoration: 'none' }} href={`${langPrefix}/blog/${getArticleSlug(article)}/`}>
                                                <img src={article.headerImageUrl} />
                                                <div className='col'>
                                                    <label>{formatDate(article.addedAt)}</label>
                                                    <h3>{getArticleTitle(article)}</h3>
                                                </div>
                                            </a>
                                        )
                                    }
                                </div>
                            </div>

                            {
                                !loading && !initialLoading &&
                                <>
                                    <div className="white-content-card articles-card">
                                        <>
                                            {
                                                articles && articles.length > 0 ?
                                                    <>
                                                        <div className="articles-grid">
                                                            {articles.map(article => (
                                                                <a
                                                                    style={{ textDecoration: 'none' }}
                                                                    key={article.id}
                                                                    className="d-flex flex-column article"
                                                                    href={`${langPrefix}/blog/${getArticleSlug(article)}/`}>
                                                                    <img src={article.headerImageUrl} />
                                                                    <label className='mt-2'>{formatDate(article.addedAt)}</label>
                                                                    <h3 className='mt-2 mb-1'>{getArticleTitle(article)}</h3>
                                                                    <small>{getPreview(getArticleContent(article))}</small>
                                                                </a>
                                                            ))}
                                                        </div>
                                                        {
                                                            totalPages > 1 &&
                                                            <div className="d-flex justify-content-center mt-4">
                                                                <Pagination
                                                                    count={totalPages}
                                                                    page={currentPage}
                                                                    onChange={handlePageChange}
                                                                    color="primary"
                                                                />
                                                            </div>
                                                        }
                                                    </>

                                                    :
                                                    <div className='d-flex justify-content-center'>
                                                        <small>No blog posts found</small>
                                                    </div>
                                            }
                                        </>

                                    </div>
                                </>
                            }

                        </div>

                    </div>
                </>

            )}
        </div>
    );
}
export default Blog;
